import { useGetGroupsQuery } from "@/store/services/supabase";
import { Input, Modal } from "antd";
import type { MutableRefObject, ReactNode } from "react";
import { useState } from "react";
import { AnalysisCollectionPermissions, GridItem } from "./AnalysisCollectionFormItems";

/** The fields of a run that the edit modal changes. */
interface EditableCollection {
  name: string;
  isPrivate: boolean;
  groupId: string;
}
export type AnalysisCollectionEditValues<T extends EditableCollection> = {
  schema: T;
  name: string;
  isPrivate: boolean;
  groupId: string;
};
export interface AnalysisCollectionEditModalProps<T extends EditableCollection> {
  schema: T;
  isOwner: boolean;
  open: boolean;
  onCancel: () => void;
  onOk: (values: AnalysisCollectionEditValues<T>) => Promise<void>;
  hasOrgKeys: boolean;
  canCreatePrivate: boolean;
  editModalRef?: MutableRefObject<null>;
}

/**
 * Renames a run or changes its permissions. Accepts editModalRef so callers (such as Portfolio's tutorial)
 * can inject it without this file depending on them.
 */
export const AnalysisCollectionEditModal = <T extends EditableCollection,>({
  schema,
  isOwner,
  open,
  onCancel,
  onOk,
  hasOrgKeys,
  canCreatePrivate,
  editModalRef = {
    current: null
  }
}: AnalysisCollectionEditModalProps<T>) => {
  const {
    data: groups
  } = useGetGroupsQuery();
  const [name, setName] = useState(schema.name);
  const [isPrivate, setIsPrivate] = useState(schema.isPrivate || false);
  const [groupId, setGroupId] = useState(schema.groupId);
  if (!groups) return null;
  const groupOpts = groups.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  })).filter(val => isOwner ? true : val.key === schema.groupId);
  const handleOk = async () => {
    await onOk({
      schema,
      groupId,
      name,
      isPrivate
    }).then(onCancel);
  };
  return <Modal title={`Edit ${schema.name}`} open={open} onCancel={onCancel} onOk={handleOk} okText="Save" modalRender={(node: ReactNode) => <div ref={editModalRef as MutableRefObject<null>}>{node}</div>} data-sentry-element="Modal" data-sentry-component="AnalysisCollectionEditModal" data-sentry-source-file="AnalysisCollectionEditModal.tsx">
      <div className="flex flex-col gap-2 rounded-lg border border-subtle p-4">
        <GridItem data-sentry-element="GridItem" data-sentry-source-file="AnalysisCollectionEditModal.tsx">
          <div>Name</div>
          <div className="col-span-2">
            <Input value={name} className="col-span-2" onChange={val => setName(val.target.value)} data-sentry-element="Input" data-sentry-source-file="AnalysisCollectionEditModal.tsx" />
          </div>
        </GridItem>

        <AnalysisCollectionPermissions isPrivate={isPrivate} setIsPrivate={setIsPrivate} enableShare={!!hasOrgKeys} groupOpts={groupOpts} groupId={groupId} setGroupId={setGroupId}
      // Allow getting out of private even when private disabled.
      hidePermissionsRadioGroup={!canCreatePrivate && !schema.isPrivate} data-sentry-element="AnalysisCollectionPermissions" data-sentry-source-file="AnalysisCollectionEditModal.tsx" />
      </div>
    </Modal>;
};